import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FaFlag,
  FaCheck,
  FaBullseye,
  FaExclamationTriangle,
  FaPlus,
  FaSyncAlt,
  FaEdit,
  FaTrashAlt,
  FaTimes,
  FaSave,
  FaCheckCircle,
  FaPauseCircle,
  FaPlayCircle,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechInput,
  TechSelect,
  TechTextarea,
  TechButton,
  TechButtonGhost,
  TechButtonDanger,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  StatusPill,
  TechBar,
  TechBarFill,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  listObjectives,
  createObjective,
  updateObjective,
  deleteObjective,
} from '../../services/platformService';

/* ---------- styled ---------- */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const Card = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $color }) => $color || accent};
  border-radius: 8px;
  padding: 1rem 1.15rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Head = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.55rem;
  margin-bottom: 0.55rem;

  h4 {
    margin: 0 0 0.3rem;
    color: var(--text-primary);
    font-size: 0.98rem;
    line-height: 1.35;
  }

  .meta {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.35rem;
`;

const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${borderSoft};
  background: transparent;
  color: var(--text-disabled);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;

  svg { font-size: 0.78rem; }

  &:hover {
    color: ${({ $danger }) => ($danger ? 'var(--alert-error)' : accent)};
    border-color: ${({ $danger }) => ($danger ? 'rgba(239,71,111,0.45)' : 'rgba(0,180,216,0.4)')};
    background: ${({ $danger }) => ($danger ? 'rgba(239,71,111,0.06)' : 'rgba(0,180,216,0.06)')};
  }
`;

const Description = styled.p`
  margin: 0 0 0.7rem;
  color: var(--text-secondary);
  font-size: 0.88rem;
  line-height: 1.5;
`;

const ProgressRow = styled.div`
  margin-bottom: 0.6rem;
  .row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }
  .label { color: var(--text-disabled); }
  .val { color: ${accent}; }
`;

const KrList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;
const KrItem = styled.li`
  display: grid;
  grid-template-columns: 1fr 60px;
  gap: 0.45rem;
  align-items: center;
  padding: 0.4rem 0.6rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  font-size: 0.84rem;
  color: var(--text-secondary);

  .v {
    font-family: ${mono};
    font-size: 0.7rem;
    text-align: right;
    color: var(--text-disabled);
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(11, 19, 32, 0.78);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  padding: 1.5rem 1rem;
`;

const ModalCard = styled.form`
  width: 100%;
  max-width: 660px;
  max-height: 88vh;
  overflow-y: auto;
  background: var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  padding: 1.1rem 1.4rem;
  position: relative;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 0.85rem;

  .label {
    display: block;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.4rem;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols || 2}, 1fr)`};
  gap: 0.85rem;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const KrInput = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 80px auto;
  gap: 0.45rem;
  margin-bottom: 0.45rem;
`;

const StatusGroup = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const STATUSES = [
  { id: 'active', label: 'Activo', color: accent, icon: <FaPlayCircle /> },
  { id: 'at_risk', label: 'En riesgo', color: 'var(--alert-error)', icon: <FaExclamationTriangle /> },
  { id: 'paused', label: 'Pausado', color: 'var(--alert-warning)', icon: <FaPauseCircle /> },
  { id: 'done', label: 'Cumplido', color: 'var(--alert-success)', icon: <FaCheckCircle /> },
];
const PRIORITIES = ['low', 'medium', 'high'];

const statusMeta = (id) => STATUSES.find((s) => s.id === id) || STATUSES[0];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const blankObjective = () => ({
  title: '',
  description: '',
  category: 'general',
  priority: 'medium',
  status: 'active',
  progress: 0,
  key_results: [],
  deadline: '',
  owner: '',
});

/* ---------- componente ---------- */

const ObjectiveModal = ({ open, item, onSave, onClose, onDelete, busy }) => {
  const [form, setForm] = useState(blankObjective());

  useEffect(() => {
    if (!open) return;
    setForm({ ...blankObjective(), ...(item || {}) });
  }, [open, item]);

  if (!open) return null;
  const isEdit = Boolean(item?.id);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updateNumber = (k) => (e) => setForm({ ...form, [k]: e.target.value === '' ? '' : Number(e.target.value) });

  const krs = form.key_results || [];
  const updateKr = (i, k, v) => {
    const next = [...krs];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, key_results: next });
  };
  const addKr = () => setForm({ ...form, key_results: [...krs, { title: '', current: 0, target: 100 }] });
  const removeKr = (i) => setForm({ ...form, key_results: krs.filter((_, idx) => idx !== i) });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave({
      ...form,
      progress: Number(form.progress) || 0,
      deadline: form.deadline || null,
    });
  };

  return (
    <Modal onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalCard onSubmit={handleSubmit}>
        <header style={{ display: 'flex', alignItems: 'center', marginBottom: '0.85rem', paddingBottom: '0.5rem', borderBottom: `1px solid ${borderSoft}` }}>
          <h2 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
            {isEdit ? 'Editar objetivo' : 'Nuevo objetivo'}
          </h2>
          <IconBtn type="button" onClick={onClose} style={{ marginLeft: 'auto' }}><FaTimes /></IconBtn>
        </header>

        <Field>
          <span className="label">Título *</span>
          <TechInput value={form.title} onChange={update('title')} placeholder="Ej: Aumentar cobertura mediática a 80%" autoFocus />
        </Field>

        <Field>
          <span className="label">Descripción</span>
          <TechTextarea value={form.description || ''} onChange={update('description')} rows={3} />
        </Field>

        <Row cols={2}>
          <Field>
            <span className="label">Categoría</span>
            <TechInput value={form.category} onChange={update('category')} placeholder="comunicacion, terreno, digital…" />
          </Field>
          <Field>
            <span className="label">Owner</span>
            <TechInput value={form.owner || ''} onChange={update('owner')} placeholder="Equipo responsable" />
          </Field>
        </Row>

        <Row cols={3}>
          <Field>
            <span className="label">Prioridad</span>
            <TechSelect value={form.priority} onChange={update('priority')}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </TechSelect>
          </Field>
          <Field>
            <span className="label">Status</span>
            <TechSelect value={form.status} onChange={update('status')}>
              {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </TechSelect>
          </Field>
          <Field>
            <span className="label">Progreso %</span>
            <TechInput type="number" min="0" max="100" value={form.progress} onChange={updateNumber('progress')} />
          </Field>
        </Row>

        <Field>
          <span className="label">Fecha límite</span>
          <TechInput type="date" value={form.deadline ? form.deadline.slice(0, 10) : ''} onChange={update('deadline')} />
        </Field>

        <Field>
          <span className="label">Key Results</span>
          {krs.length === 0 && (
            <p style={{ color: 'var(--text-disabled)', fontSize: '0.78rem', margin: '0 0 8px' }}>
              Sin KRs todavía. Agregá uno para medir el progreso.
            </p>
          )}
          {krs.map((kr, i) => (
            <KrInput key={i}>
              <TechInput placeholder="Resultado clave" value={kr.title || ''} onChange={(e) => updateKr(i, 'title', e.target.value)} />
              <TechInput type="number" placeholder="actual" value={kr.current ?? ''} onChange={(e) => updateKr(i, 'current', Number(e.target.value))} />
              <TechInput type="number" placeholder="meta" value={kr.target ?? ''} onChange={(e) => updateKr(i, 'target', Number(e.target.value))} />
              <IconBtn type="button" $danger onClick={() => removeKr(i)}><FaTrashAlt /></IconBtn>
            </KrInput>
          ))}
          <TechButtonGhost type="button" onClick={addKr} style={{ marginTop: 4 }}>
            <FaPlus /> Agregar KR
          </TechButtonGhost>
        </Field>

        <footer style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${borderSoft}` }}>
          {isEdit ? (
            <TechButtonDanger type="button" onClick={() => onDelete(item.id)}>
              <FaTrashAlt /> Eliminar
            </TechButtonDanger>
          ) : <span />}
          <div style={{ display: 'flex', gap: 6 }}>
            <TechButtonGhost type="button" onClick={onClose}>Cancelar</TechButtonGhost>
            <TechButton type="submit" disabled={busy}>
              <FaSave /> {busy ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear'}
            </TechButton>
          </div>
        </footer>
      </ModalCard>
    </Modal>
  );
};

const CampaignObjectives = () => {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [editor, setEditor] = useState({ open: false, item: null });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoadState({ loading: true, error: null });
    listObjectives()
      .then((data) => { setItems(data); setLoadState({ loading: false, error: null }); })
      .catch((e) => setLoadState({ loading: false, error: e?.response?.data?.error || e?.message || 'Error' }));
  };

  useEffect(load, []);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      if (editor.item?.id) {
        await updateObjective(editor.item.id, payload);
      } else {
        await createObjective(payload);
      }
      setEditor({ open: false, item: null });
      load();
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || 'Error guardando');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar objetivo?')) return;
    setBusy(true);
    try {
      await deleteObjective(id);
      setEditor({ open: false, item: null });
      load();
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || 'Error eliminando');
    } finally {
      setBusy(false);
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((i) => i.status === 'active').length;
    const done = items.filter((i) => i.status === 'done').length;
    const atRisk = items.filter((i) => i.status === 'at_risk').length;
    const avgProgress = total > 0 ? Math.round(items.reduce((a, i) => a + (i.progress || 0), 0) / total) : 0;
    return { total, active, done, atRisk, avgProgress };
  }, [items]);

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader title="Objetivos de Campaña" dossier="OKR://OBJ/v2" />
        <Loading>Cargando objetivos</Loading>
      </TechShell>
    );
  }
  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="Objetivos de Campaña" dossier="OKR://OBJ/v2" />
        <ErrorBox>No se pudo cargar · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Objetivos de Campaña"
        subtitle="Las metas de la campaña con resultados medibles. Cada objetivo queda guardado y disponible para todo el equipo."
        dossier="OKR://OBJ/v2"
        actions={
          <>
            <TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>
            <TechButton type="button" onClick={() => setEditor({ open: true, item: null })}>
              <FaPlus /> Nuevo objetivo
            </TechButton>
          </>
        }
      >
        <StatusPill>Total <strong>{stats.total}</strong></StatusPill>
        <StatusPill>Activos <strong>{stats.active}</strong></StatusPill>
        <StatusPill>Cumplidos <strong>{stats.done}</strong></StatusPill>
        {stats.atRisk > 0 && <StatusPill>En riesgo <strong>{stats.atRisk}</strong></StatusPill>}
      </TechPageHeader>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaFlag /> Objetivos activos</StatLabel>
          <StatValue>{stats.active}</StatValue>
          <StatSub>de {stats.total} totales</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCheck /> Cumplidos</StatLabel>
          <StatValue>{stats.done}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel><FaExclamationTriangle /> En riesgo</StatLabel>
          <StatValue>{stats.atRisk}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel><FaBullseye /> Progreso promedio</StatLabel>
          <StatValue>{stats.avgProgress}%</StatValue>
        </StatTile>
      </StatStrip>

      {items.length === 0 ? (
        <TechSection title="Sin objetivos cargados">
          <Empty>
            Empezá creando tu primer objetivo. Queda guardado y disponible para todo el equipo.
          </Empty>
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <TechButton type="button" onClick={() => setEditor({ open: true, item: null })}>
              <FaPlus /> Crear primer objetivo
            </TechButton>
          </div>
        </TechSection>
      ) : (
        <Grid>
          {items.map((it) => {
            const meta = statusMeta(it.status);
            return (
              <Card key={it.id} $color={meta.color}>
                <Head>
                  <div>
                    <h4>{it.title}</h4>
                    <div className="meta">
                      <TechChip>{it.category}</TechChip>
                      <TechChip tone={it.priority === 'high' ? 'danger' : it.priority === 'medium' ? 'warn' : 'mute'}>
                        {it.priority}
                      </TechChip>
                      <span style={{ color: meta.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {meta.icon} {meta.label}
                      </span>
                      {it.deadline && <span>vence · {fmtDate(it.deadline)}</span>}
                    </div>
                  </div>
                  <ActionsRow>
                    <IconBtn type="button" onClick={() => setEditor({ open: true, item: it })}><FaEdit /></IconBtn>
                    <IconBtn type="button" $danger onClick={() => handleDelete(it.id)}><FaTrashAlt /></IconBtn>
                  </ActionsRow>
                </Head>

                {it.description && <Description>{it.description}</Description>}

                <ProgressRow>
                  <div className="row">
                    <span className="label">Progreso</span>
                    <span className="val">{it.progress}%</span>
                  </div>
                  <TechBar>
                    <TechBarFill value={it.progress || 0} color={meta.color} />
                  </TechBar>
                </ProgressRow>

                {Array.isArray(it.key_results) && it.key_results.length > 0 && (
                  <KrList>
                    {it.key_results.map((kr, i) => {
                      const pct = kr.target ? Math.round((kr.current / kr.target) * 100) : 0;
                      return (
                        <KrItem key={i}>
                          <div>
                            <div>{kr.title}</div>
                            <TechBar style={{ marginTop: 4, height: 4 }}>
                              <TechBarFill value={pct} />
                            </TechBar>
                          </div>
                          <span className="v">{kr.current}/{kr.target}</span>
                        </KrItem>
                      );
                    })}
                  </KrList>
                )}

                {it.owner && (
                  <p style={{ marginTop: 8, fontFamily: mono, fontSize: '0.6rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
                    owner · {it.owner}
                  </p>
                )}
              </Card>
            );
          })}
        </Grid>
      )}

      <ObjectiveModal
        open={editor.open}
        item={editor.item}
        onClose={() => setEditor({ open: false, item: null })}
        onSave={handleSave}
        onDelete={handleDelete}
        busy={busy}
      />
    </TechShell>
  );
};

export default CampaignObjectives;
